// src/components/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { Layout, Menu, Tooltip } from 'antd';
import {
  UserOutlined, 
  TeamOutlined, 
  CarOutlined, 
  FileOutlined,
  MoneyCollectOutlined, 
  ContainerOutlined, 
  CopyOutlined,
  FileTextOutlined, 
  DashboardOutlined,
  ShopOutlined,
  SettingOutlined,
  SecurityScanOutlined,
  IdcardOutlined,
  DownOutlined,
  RightOutlined
} from '@ant-design/icons';
import { Link, useLocation } from 'react-router-dom';

const { Sider } = Layout;

const Sidebar = ({ collapsed }) => {
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState('dashboard');
  
  // Initialiser les sous-menus ouverts basés sur l'URL
  useEffect(() => {
    const path = location.pathname;
    const activeKeys = [];
    
    // Déterminer quel sous-menu doit être ouvert selon l'URL active
    if (path.includes('/chauffeur') || path.includes('/clients') || 
        path.includes('/fournisseurs') || path.includes('/personnels')) {
      activeKeys.push('sub1');
    }
    if (path.includes('/vehicules') || path.includes('/postes')) {
      activeKeys.push('sub2');
    }
    if (path.includes('/achat') || path.includes('/vente')) {
      activeKeys.push('sub3');
    }
    if (path.includes('/commandes') || path.includes('/missions') || path.includes('/articles')) {
      activeKeys.push('sub4');
    }
    if (path.includes('/settings')) {
      activeKeys.push('sub5');
    }
    
    setOpenKeys(activeKeys);
    
    // Mettre à jour la clé sélectionnée
    const activeKey = findActiveKey(path);
    setSelectedKey(activeKey);
  }, [location.pathname]);

  // Trouver la clé active basée sur l'URL
  const findActiveKey = (path = null) => {
    const currentPath = path || location.pathname;
    if (currentPath === '/dashboard') return 'dashboard';
    if (currentPath.includes('/chauffeur')) return '1';
    if (currentPath.includes('/clients')) return '2';
    if (currentPath.includes('/fournisseurs')) return '11';
    if (currentPath.includes('/personnels')) return '3';
    if (currentPath.includes('/vehicules')) return '4';
    if (currentPath.includes('/postes')) return '5';
    if (currentPath.includes('/achat')) return '6';
    if (currentPath.includes('/vente')) return '7';
    if (currentPath.includes('/commandes')) return '8';
    if (currentPath.includes('/missions')) return '9';
    if (currentPath.includes('/articles')) return '10';
    if (currentPath.includes('/settings/users')) return '12';
    if (currentPath.includes('/settings/roles')) return '13';
    if (currentPath.includes('/settings/permissions')) return '14';
    return 'dashboard';
  };

  // Gérer l'ouverture/fermeture des sous-menus
  const handleOpenChange = (keys) => {
    // Ne garder que le dernier sous-menu ouvert
    const latestOpenKey = keys.find(key => openKeys.indexOf(key) === -1);
    
    // Vérifier si la clé existe dans les éléments de menu
    const menuItemsArray = createMenuItems();
    if (latestOpenKey && menuItemsArray.find(item => item.key === latestOpenKey)) {
      setOpenKeys([latestOpenKey]);
    } else {
      setOpenKeys([]);
    }
  };

  // Fonction pour créer des éléments de menu
  const createMenuItems = () => {
    const baseMenuItems = [
      {
        key: 'dashboard',
        icon: <DashboardOutlined />,
        label: <Link to="/dashboard" style={{ textDecoration: 'none', color: 'inherit' }}>Tableau de bord</Link>,
      },
      {
        key: 'sub1',
        icon: <UserOutlined />,
        label: 'Gestion Personnel',
        onTitleClick: ({ key }) => {
          if (openKeys.includes(key)) {
            setOpenKeys(openKeys.filter(k => k !== key));
          } else {
            setOpenKeys([key]);
          }
        },
        children: [
          {
            key: '1',
            icon: <UserOutlined />,
            label: <Link to="/chauffeur" style={{ textDecoration: 'none', color: 'inherit' }}>Chauffeurs</Link>,
          },
          {
            key: '2',
            icon: <TeamOutlined />,
            label: <Link to="/clients" style={{ textDecoration: 'none', color: 'inherit' }}>Clients</Link>,
          },
          {
            key: '11',
            icon: <ShopOutlined />,
            label: <Link to="/fournisseurs" style={{ textDecoration: 'none', color: 'inherit' }}>Fournisseurs</Link>,
          },
          {
            key: '3',
            icon: <UserOutlined />,
            label: <Link to="/personnels" style={{ textDecoration: 'none', color: 'inherit' }}>Employés</Link>,
          },
        ],
      },
      {
        key: 'sub2',
        icon: <CarOutlined />,
        label: 'Gestion Transports',
        onTitleClick: ({ key }) => {
          if (openKeys.includes(key)) {
            setOpenKeys(openKeys.filter(k => k !== key));
          } else {
            setOpenKeys([key]);
          }
        },
        children: [
          {
            key: '4',
            icon: <CarOutlined />,
            label: <Link to="/vehicules" style={{ textDecoration: 'none', color: 'inherit' }}>Véhicules</Link>,
          },
          {
            key: '5',
            icon: <ContainerOutlined />,
            label: <Link to="/postes" style={{ textDecoration: 'none', color: 'inherit' }}>Postes</Link>,
          },
        ],
      },
      {
        key: 'sub3',
        icon: <MoneyCollectOutlined />,
        label: 'Gestion Cimenterie',
        onTitleClick: ({ key }) => {
          if (openKeys.includes(key)) {
            setOpenKeys(openKeys.filter(k => k !== key));
          } else {
            setOpenKeys([key]);
          }
        },
        children: [
          {
            key: '6',
            icon: <MoneyCollectOutlined />,
            label: <Link to="/achat" style={{ textDecoration: 'none', color: 'inherit' }}>Achat Ciment</Link>,
          },
          {
            key: '7',
            icon: <MoneyCollectOutlined />,
            label: <Link to="/vente" style={{ textDecoration: 'none', color: 'inherit' }}>Vente Ciment</Link>,
          },
        ],
      },
    
      {
        key: 'sub4',
        icon: <FileTextOutlined />,
        label: 'Documents',
        onTitleClick: ({ key }) => {
          if (openKeys.includes(key)) {
            setOpenKeys(openKeys.filter(k => k !== key));
          } else {
            setOpenKeys([key]);
          }
        },
        children: [
          {
            key: '8',
            icon: <FileTextOutlined />,
            label: <Link to="/commandes" style={{ textDecoration: 'none', color: 'inherit' }}>Commandes clients</Link>,
          },
          {
            key: '9',
            icon: <FileOutlined />,
            label: <Link to="/missions" style={{ textDecoration: 'none', color: 'inherit' }}>Missions</Link>,
          },
          {
            key: '10',
            icon: <CopyOutlined />,
            label: <Link to="/articles" style={{ textDecoration: 'none', color: 'inherit' }}>Articles</Link>,
          }
        ],
      },
      {
        key: 'sub5',
        icon: <SettingOutlined />,
        label: 'Réglage',
        onTitleClick: ({ key }) => {
          if (openKeys.includes(key)) {
            setOpenKeys(openKeys.filter(k => k !== key));
          } else {
            setOpenKeys([key]);
          }
        },
        children: [
          {
            key: '12',
            icon: <UserOutlined />,
            label: <Link to="/settings/users" style={{ textDecoration: 'none', color: 'inherit' }}>Gestion des utilisateurs</Link>,
          },
          {
            key: '13',
            icon: <IdcardOutlined />,
            label: <Link to="/settings/roles" style={{ textDecoration: 'none', color: 'inherit' }}>Gestion des rôles</Link>,
          },
          {
            key: '14',
            icon: <SecurityScanOutlined />,
            label: <Link to="/settings/permissions" style={{ textDecoration: 'none', color: 'inherit' }}>Gestion des autorisations</Link>,
          },
        ],
      },
      
    ];

    // Si la sidebar est réduite, afficher les éléments avec tooltip
    if (collapsed) {
      return baseMenuItems.map(item => {
        if (item.children) {
          return {
            ...item,
            label: (
              <Tooltip title={item.label} placement="right">
                <div style={{ textAlign: 'center' }}>
                  {item.icon}
                </div>
              </Tooltip>
            ),
            children: item.children.map(child => ({
              ...child,
              label: (
                <Tooltip title={child.label.props?.children || child.label} placement="right">
                  {child.label}
                </Tooltip>
              )
            }))
          };
        }
        return {
          ...item,
          label: (
            <Tooltip title={item.label.props?.children || item.label} placement="right">
              {item.label}
            </Tooltip>
          )
        };
      });
    }

    return baseMenuItems;
  };

  // Fonction pour l'icône de flèche personnalisée
  const customExpandIcon = ({ isOpen }) => {
    if (collapsed) return null;
    return isOpen ? 
      <DownOutlined style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.65)' }} /> : 
      <RightOutlined style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.65)' }} />;
  };

  // Gérer le clic sur les éléments de menu
  const handleMenuClick = ({ key, domEvent }) => {
    // Empêcher l'ouverture du sous-menu au clic sur le label
    if (key.startsWith('sub')) {
      domEvent.stopPropagation();
      // Gérer l'ouverture/fermeture manuellement
      if (openKeys.includes(key)) {
        setOpenKeys([]);
      } else {
        setOpenKeys([key]);
      }
    }
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      trigger={null}
      width={250}
      collapsedWidth={80}
      style={{
        background: '#222d3dff',
        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.15)',
        position: 'fixed',
        height: 'calc(100vh - 64px)',
        left: 0,
        top: 64,
        overflow: 'auto',
        zIndex: 999,
        transition: 'all 0.2s',
      }}
      theme="dark"
      className="custom-sidebar"
    >
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        openKeys={collapsed ? [] : openKeys}
        onOpenChange={handleOpenChange}
        items={createMenuItems()}
        onClick={handleMenuClick}
        style={{
          background: 'transparent',
          borderRight: 0,
          padding: '16px 0',
          transition: 'all 0.3s',
        }}
        theme="dark"
        inlineCollapsed={collapsed}
        expandIcon={customExpandIcon}
        // Forcer l'ouverture seulement au clic sur l'icône flèche
        triggerSubMenuAction="click"
      />
      
      {/* Footer du sidebar */}
      {!collapsed && (
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'rgba(0, 0, 0, 0.2)',
          transition: 'all 0.3s',
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '12px',
          textAlign: 'center'
        }}>
          Version 1.0.0
        </div>
      )}

      <style>
        {`
          .custom-sidebar .ant-menu {
            background: transparent !important;
          }
          
          .custom-sidebar .ant-menu-item,
          .custom-sidebar .ant-menu-submenu-title {
            height: 48px !important;
            line-height: 48px !important;
            margin: 4px 0 !important;
            padding: 0 16px !important;
            border-radius: 8px !important;
            transition: all 0.3s ease !important;
          }
          
          .custom-sidebar .ant-menu-item:hover,
          .custom-sidebar .ant-menu-submenu-title:hover {
            background: rgba(255, 255, 255, 0.1) !important;
          }
          
          .custom-sidebar .ant-menu-item-selected {
            background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%) !important;
            box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3) !important;
          }
          
          .custom-sidebar .ant-menu-item-selected::after {
            display: none !important;
          }
          
          .custom-sidebar .ant-menu-submenu-selected > .ant-menu-submenu-title {
            color: #fff !important;
            background: rgba(24, 144, 255, 0.15) !important;
          }
          
          .custom-sidebar .ant-menu-submenu-open > .ant-menu-submenu-title {
            color: #fff !important;
          }
          
          .custom-sidebar .ant-menu-submenu-arrow {
            opacity: 1 !important;
          }
          
          .custom-sidebar .ant-menu-inline .ant-menu-item {
            padding-left: 50px !important;
          }
          
          .custom-sidebar .ant-menu-inline-collapsed .ant-menu-item {
            padding: 0 24px !important;
          }
          
          .custom-sidebar .ant-menu-inline-collapsed .ant-menu-submenu-title {
            padding: 0 24px !important;
          }
          
          /* Désactiver l'ouverture au clic sur le label */
          .custom-sidebar .ant-menu-submenu-title {
            cursor: default !important;
          }
          
          .custom-sidebar .ant-menu-submenu-title:hover .ant-menu-submenu-arrow {
            color: #fff !important;
          }
          
          /* Permettre l'ouverture seulement via la flèche */
          .custom-sidebar .ant-menu-submenu-arrow {
            cursor: pointer !important;
          }
          
          /* Style pour les sous-menus */
          .custom-sidebar .ant-menu-sub {
            background: rgba(0, 0, 0, 0.2) !important;
            border-radius: 8px !important;
            margin: 4px 0 !important;
            padding: 8px 0 !important;
          }
          
          .custom-sidebar .ant-menu-sub .ant-menu-item {
            margin: 2px 8px !important;
            height: 40px !important;
            line-height: 40px !important;
            border-radius: 6px !important;
          }
          
          .custom-sidebar .ant-menu-sub .ant-menu-item:hover {
            background: rgba(255, 255, 255, 0.08) !important;
          }
          
          .custom-sidebar .ant-menu-sub .ant-menu-item-selected {
            background: rgba(24, 144, 255, 0.2) !important;
            border-left: 3px solid #1890ff !important;
          }
          
          /* Style pour le mode réduit */
          .custom-sidebar .ant-menu-inline-collapsed .ant-menu-submenu-arrow {
            display: none !important;
          }
          
          .custom-sidebar .ant-menu-inline-collapsed .ant-tooltip {
            max-width: 200px !important;
          }
          
          .custom-sidebar .ant-menu-inline-collapsed .ant-tooltip-inner {
            background: rgba(0, 0, 0, 0.8) !important;
            border-radius: 6px !important;
            padding: 8px 12px !important;
            font-size: 14px !important;
          }
          
          /* Style pour les liens */
          .custom-sidebar a {
            display: block !important;
            width: 100% !important;
            height: 100% !important;
          }
        `}
      </style>
    </Sider>
  );
};

export default Sidebar;